// src/components/ShippingForm.jsx
import React, { useState } from 'react';
import { FaTimes, FaTruck, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import '../styles/ShippingForm.css';
const ShippingForm = ({ pack, user, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    phone: '+34 ',
    address: '',
    city: '',
    postal_code: '',
    country: 'España'
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validación básica
  const validateField = (name, value) => {
    switch (name) {
      case 'full_name':
        if (!value.trim()) return 'El nombre es requerido';
        if (value.trim().length < 2) return 'Nombre demasiado corto';
        return '';
      
      case 'email':
        if (!value.trim()) return 'El email es requerido';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Email inválido';
        return '';
      
      case 'phone':
        if (!value.trim()) return 'El teléfono es requerido';
        // Quitar +34 para validar solo los dígitos después
        let phoneNumber = value.replace('+34 ', '').replace(/\s/g, '');
        if (phoneNumber.length < 9) return 'Teléfono inválido (mínimo 9 dígitos después del +34)';
        if (!/^[0-9]+$/.test(phoneNumber)) return 'Solo números después del +34';
        return '';
      
      case 'address':
        if (!value.trim()) return 'La dirección es requerida';
        if (value.trim().length < 5) return 'Dirección demasiado corta';
        return '';
      
      case 'city':
        if (!value.trim()) return 'La ciudad es requerida';
        if (value.trim().length < 2) return 'Ciudad inválida';
        return '';
      
      case 'postal_code':
        if (!value.trim()) return 'El código postal es requerido';
        const postalRegex = /^[0-9]{5}$/;
        if (!postalRegex.test(value.trim())) return 'Código postal inválido (5 dígitos)';
        return '';
      
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Manejo especial para teléfono: mantener +34 al inicio
    if (name === 'phone') {
      let newValue = value;
      if (!newValue.startsWith('+34 ')) {
        newValue = '+34 ';
      }
      // Permitir que el usuario escriba después de +34
      const afterPrefix = newValue.replace('+34 ', '');
      if (/^\d*$/.test(afterPrefix)) {
        setFormData(prev => ({ ...prev, [name]: newValue }));
        const error = validateField(name, newValue);
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar todos los campos
    const newErrors = {};
    let isValid = true;
    
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    setTouched(Object.keys(formData).reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    
    if (isValid) {
      setIsSubmitting(true);
      try {
        // Limpiar el teléfono para enviar (solo números después del +34)
        const cleanPhone = formData.phone.replace('+34 ', '').replace(/\s/g, '');
        
        const shippingData = {
          full_name: formData.full_name,
          phone: cleanPhone,
          address: formData.address,
          city: formData.city,
          postal_code: formData.postal_code,
          country: formData.country
        };
        
        await onSuccess(shippingData);
      } catch (err) {
        console.error('Error:', err);
        alert('Error al procesar el envío');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="shipping-modal-overlay">
      <div className="shipping-modal">
        <button className="shipping-modal-close" onClick={onCancel}>
          <FaTimes />
        </button>
        
        <div className="shipping-modal-header">
          <h2><FaTruck /> Información de Envío</h2>
          <p>Completa tus datos para recibir tu pedido</p>
        </div>
        
        <form onSubmit={handleSubmit} className="shipping-form">
          {/* Resumen del pack */}
          <div className="pack-summary-modal">
            <span className="pack-name-modal">{pack.name}</span>
            <span className="pack-price-modal">€{pack.price}</span>
            {pack.bgpTokens > 0 && (
              <span className="pack-bonus-modal">+{pack.bgpTokens} BGP</span>
            )}
          </div>

          <div className="form-group">
            <label>Nombre completo *</label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`form-input ${touched.full_name && errors.full_name ? 'error' : ''}`}
              placeholder="Ej: Juan Pérez"
            />
            {touched.full_name && errors.full_name && (
              <span className="error-message">{errors.full_name}</span>
            )}
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`form-input ${touched.email && errors.email ? 'error' : ''}`}
              placeholder="ejemplo@email.com"
              disabled
            />
            {touched.email && errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label>Teléfono *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`form-input ${touched.phone && errors.phone ? 'error' : ''}`}
              placeholder="+34 611701132"
            />
            {touched.phone && errors.phone && (
              <span className="error-message">{errors.phone}</span>
            )}
            <small className="field-hint">Formato: +34 123456789</small>
          </div>

          <div className="form-group">
            <label>Dirección *</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`form-input ${touched.address && errors.address ? 'error' : ''}`}
              placeholder="Calle, número, piso"
            />
            {touched.address && errors.address && (
              <span className="error-message">{errors.address}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Código Postal *</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength="5"
                className={`form-input ${touched.postal_code && errors.postal_code ? 'error' : ''}`}
                placeholder="43890"
              />
              {touched.postal_code && errors.postal_code && (
                <span className="error-message">{errors.postal_code}</span>
              )}
            </div>

            <div className="form-group">
              <label>Ciudad *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-input ${touched.city && errors.city ? 'error' : ''}`}
                placeholder="L'Hospitalet de l'Infant"
              />
              {touched.city && errors.city && (
                <span className="error-message">{errors.city}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>País *</label>
            <input
              type="text"
              name="country"
              value="España"
              disabled
              className="form-input disabled"
              style={{ opacity: 0.7, cursor: 'not-allowed' }}
            />
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? (
              <><FaSpinner className="spinning-icon" /> Procesando...</>
            ) : (
              <><FaCheckCircle /> Continuar al pago</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ShippingForm;